import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './TagInput.css';

interface Tag {
  _id: string;
  name: string;
  displayName: string;
  category: string;
  color: string;
  usageCount: number;
}

interface TagInputProps {
  value: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  maxTags?: number;
  disabled?: boolean;
}

const TagInput: React.FC<TagInputProps> = ({
  value = [],
  onChange,
  placeholder = "Add tags...",
  maxTags = 10,
  disabled = false
}) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<Tag[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  const apiUrl = process.env.REACT_APP_API_URL || window.location.origin;

  // Debounced search for suggestions
  useEffect(() => {
    const searchTags = async () => {
      if (inputValue.length < 1) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await axios.get(`${apiUrl}/api/tags/suggestions?q=${encodeURIComponent(inputValue)}&limit=8`);
        if (response.data.success) {
          // Filter out already selected tags
          const availableSuggestions = response.data.data.suggestions.filter(
            (tag: Tag) => !value.includes(tag.name)
          );
          setSuggestions(availableSuggestions);
          setShowSuggestions(availableSuggestions.length > 0);
          setActiveSuggestion(-1);
        }
      } catch (error) {
        console.error('Error fetching tag suggestions:', error);
        setSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchTags, 300);
    return () => clearTimeout(debounceTimer);
  }, [inputValue, value, apiUrl]);

  const addTag = async (tagName: string) => {
    if (!tagName.trim() || value.includes(tagName.toLowerCase()) || value.length >= maxTags) {
      return;
    }

    const newTags = [...value, tagName.toLowerCase()];
    onChange(newTags);
    setInputValue('');
    setSuggestions([]);
    setShowSuggestions(false);
    inputRef.current?.focus();

    // If tag doesn't exist in database, create it
    const existingTag = suggestions.find(tag => tag.name === tagName.toLowerCase());
    if (!existingTag && inputValue.length > 0) {
      try {
        await axios.post(`${apiUrl}/api/tags`, {
          name: tagName.toLowerCase(),
          displayName: tagName,
          category: 'other'
        });
      } catch (error) {
        console.log('Tag may already exist or creation failed:', error);
      }
    }
  };

  const removeTag = (tagToRemove: string) => {
    const newTags = value.filter(tag => tag !== tagToRemove);
    onChange(newTags);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeSuggestion >= 0 && suggestions[activeSuggestion]) {
        addTag(suggestions[activeSuggestion].name);
      } else if (inputValue.trim()) {
        addTag(inputValue.trim());
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveSuggestion(prev => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveSuggestion(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setActiveSuggestion(-1);
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeTag(value[value.length - 1]);
    } else if (e.key === ',') {
      e.preventDefault();
      if (inputValue.trim()) {
        addTag(inputValue.trim());
      }
    }
  };

  const handleSuggestionClick = (tag: Tag) => {
    addTag(tag.name);
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      blockchain: '#f59e0b',
      technology: '#6366f1',
      tool: '#10b981',
      framework: '#f97316',
      language: '#8b5cf6',
      platform: '#06b6d4',
      other: '#6b7280'
    };
    return colors[category as keyof typeof colors] || colors.other;
  };

  return (
    <div className="tag-input-container">
      <div className={`tag-input-wrapper ${disabled ? 'disabled' : ''}`}>
        <div className="selected-tags">
          {value.map((tag, index) => (
            <span key={index} className="tag-chip">
              {tag}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className="tag-remove"
                  aria-label={`Remove ${tag} tag`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => inputValue && setShowSuggestions(suggestions.length > 0)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={value.length === 0 ? placeholder : ''}
          disabled={disabled || value.length >= maxTags}
          className="tag-input"
        />
        
        {isLoading && (
          <div className="tag-loading">
            <div className="loading-spinner"></div>
          </div>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div ref={suggestionsRef} className="tag-suggestions">
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion._id}
              onClick={() => handleSuggestionClick(suggestion)}
              className={`tag-suggestion ${index === activeSuggestion ? 'active' : ''}`}
            >
              <span className="suggestion-text">
                <span 
                  className="category-indicator"
                  style={{ backgroundColor: suggestion.color }}
                ></span>
                {suggestion.displayName}
                <span className="category-label">
                  {suggestion.category}
                </span>
              </span>
              {suggestion.usageCount > 0 && (
                <span className="usage-count">{suggestion.usageCount}</span>
              )}
            </div>
          ))}
          
          {inputValue && !suggestions.some(s => s.name.toLowerCase() === inputValue.toLowerCase()) && (
            <div
              onClick={() => addTag(inputValue)}
              className={`tag-suggestion create-new ${suggestions.length === activeSuggestion ? 'active' : ''}`}
            >
              <span className="suggestion-text">
                <span className="create-icon">+</span>
                Create "{inputValue}"
              </span>
            </div>
          )}
        </div>
      )}

      {value.length >= maxTags && (
        <div className="tag-limit-message">
          Maximum {maxTags} tags allowed
        </div>
      )}
    </div>
  );
};

export default TagInput;
